// OrderBoardingContext.js
import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

const OrderBoardingContext = createContext();

export const useOrderBoarding = () => {
  const context = useContext(OrderBoardingContext);
  if (!context) throw new Error('useOrderBoarding must be used within OrderBoardingProvider');
  return context;
};

export const OrderBoardingProvider = ({ children }) => {
  const [boardingItems, setBoardingItems] = useState([]);

  // Replace the entire list (used for syncing from parent)
  const syncBoardingItems = useCallback((items) => {
    setBoardingItems(items);
  }, []);

  // Helper: date overlap
  const isOverlap = (start1, end1, start2, end2) => {
    return start1 <= end2 && end1 >= start2;
  };

  // Check if a room is occupied by any boarding item (excluding optional localIndex)
  const isRoomOccupied = useCallback((roomId, checkin, checkout, excludeLocalIndex = null) => {
    return boardingItems.some(item => {  
      if (excludeLocalIndex != null && item._localIndex === excludeLocalIndex) return false;
      if (item.room_id !== roomId) return false;
      if (!item.start_date || !item.end_date) return false;
      return isOverlap(checkin, checkout, item.start_date, item.end_date);
    });
  }, [boardingItems]);

  // Filter a list of rooms (from backend) to exclude occupied ones
  const filterAvailableRooms = useCallback((rooms, checkin, checkout, excludeLocalIndex = null) => {
    return rooms.filter(room => !isRoomOccupied(room._id, checkin, checkout, excludeLocalIndex));
  }, [isRoomOccupied]);

  const value = useMemo(() =>({
    boardingItems,
    syncBoardingItems,
    isRoomOccupied,
    filterAvailableRooms,
  }), [boardingItems, syncBoardingItems, isRoomOccupied, filterAvailableRooms]);

  return (
    <OrderBoardingContext.Provider value={value}>
      {children}
    </OrderBoardingContext.Provider>
  );
};